import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  ListItemText,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

const paletteByTone = {
  purple: {
    bg: "linear-gradient(135deg, #f3e8ff, #ede9fe)",
    color: "#7c3aed",
    button: "linear-gradient(135deg, #7c3aed, #6d28d9)",
  },
  blue: {
    bg: "linear-gradient(135deg, #dbeafe, #eff6ff)",
    color: "#2563eb",
    button: "linear-gradient(135deg, #2563eb, #1d4ed8)",
  },
  green: {
    bg: "linear-gradient(135deg, #dcfce7, #ecfdf5)",
    color: "#16a34a",
    button: "linear-gradient(135deg, #16a34a, #15803d)",
  },
  red: {
    bg: "linear-gradient(135deg, #fee2e2, #fff1f2)",
    color: "#ef4444",
    button: "linear-gradient(135deg, #ef4444, #dc2626)",
  },
};

const defaultIcons = {
  purple: LockOutlinedIcon,
  blue: SendRoundedIcon,
  green: CheckCircleOutlineRoundedIcon,
  red: DeleteOutlineRoundedIcon,
};

export function BrandDialogFrame({
  open,
  onClose,
  title,
  subtitle,
  tone = "blue",
  icon: IconComponent,
  maxWidth = "sm",
  children,
  footer,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const toneConfig = paletteByTone[tone] || paletteByTone.blue;
  const HeaderIcon = IconComponent || defaultIcons[tone] || defaultIcons.blue;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={maxWidth}
      fullScreen={fullScreen}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(15, 23, 42, 0.36)",
            backdropFilter: "blur(6px)",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 4,
          overflow: "hidden",
          boxShadow: "0 32px 80px rgba(15, 23, 42, 0.22)",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
          minHeight: fullScreen ? "100dvh" : undefined,
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: fullScreen ? "100%" : undefined }}>
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2.25,
            borderBottom: "1px solid #e2e8f0",
            background:
              "radial-gradient(circle at top left, rgba(219,234,254,0.85), rgba(255,255,255,0.98) 40%), #fff",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  display: "grid",
                  placeItems: "center",
                  background: toneConfig.bg,
                  color: toneConfig.color,
                  border: "1px solid rgba(255,255,255,0.85)",
                  flexShrink: 0,
                }}
              >
                <HeaderIcon />
              </Box>
              <Box>
                <Typography sx={{ fontSize: { xs: 24, sm: 34 }, fontWeight: 900, color: "#0f172a", lineHeight: 1.08 }}>
                  {title}
                </Typography>
                {subtitle ? (
                  <Typography sx={{ mt: 0.55, color: "#64748b", fontSize: 14.5 }}>
                    {subtitle}
                  </Typography>
                ) : null}
              </Box>
            </Stack>

            <IconButton onClick={onClose} sx={{ color: "#475569" }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        <DialogContent
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
            backgroundColor: "#f8fafc",
            flex: 1,
          }}
        >
          {children}
        </DialogContent>

        {footer}
      </Box>
    </Dialog>
  );
}

export function BrandDialogFooter({
  onCancel,
  onConfirm,
  cancelLabel = "Cancel",
  confirmLabel = "Save",
  confirmTone = "blue",
  loading = false,
  loadingLabel = "Saving...",
  confirmIcon,
  disabled = false,
}) {
  const toneConfig = paletteByTone[confirmTone] || paletteByTone.blue;

  return (
    <Box
      sx={{
        p: 2,
        borderTop: "1px solid #e2e8f0",
        background: "rgba(255,255,255,0.94)",
        backdropFilter: "blur(14px)",
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{
            borderRadius: 3,
            px: 2.25,
            py: 1.15,
            minWidth: 120,
            fontWeight: 800,
            color: "#334155",
            borderColor: "#cbd5e1",
          }}
        >
          {cancelLabel}
        </Button>

        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={disabled || loading}
          startIcon={confirmIcon}
          sx={{
            borderRadius: 3,
            px: 2.4,
            py: 1.15,
            minWidth: 150,
            fontWeight: 800,
            background: toneConfig.button,
            boxShadow: "0 16px 32px rgba(15, 23, 42, 0.18)",
          }}
        >
          {loading ? loadingLabel : confirmLabel}
        </Button>
      </Stack>
    </Box>
  );
}

export function BrandConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  description,
  confirmLabel,
  tone = "green",
  loading = false,
}) {
  const IconComponent = tone === "red" ? DeleteOutlineRoundedIcon : CheckCircleOutlineRoundedIcon;

  return (
    <BrandDialogFrame
      open={open}
      onClose={onClose}
      title={title}
      subtitle={description}
      tone={tone}
      icon={IconComponent}
      maxWidth="xs"
      footer={
        <BrandDialogFooter
          onCancel={onClose}
          onConfirm={onConfirm}
          confirmLabel={confirmLabel}
          confirmTone={tone}
          loading={loading}
          loadingLabel={tone === "red" ? "Deleting..." : "Processing..."}
        />
      }
    >
      <Stack spacing={2.25} alignItems="center" textAlign="center" sx={{ py: 1 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 800, color: "#0f172a" }}>{message}</Typography>
        <Alert
          severity={tone === "red" ? "error" : "success"}
          sx={{ width: "100%", borderRadius: 3 }}
        >
          This action cannot be undone.
        </Alert>
      </Stack>
    </BrandDialogFrame>
  );
}

export function BrandResetPasswordDialog({
  open,
  onClose,
  newPassword,
  setNewPassword,
  confirmPassword,
  setConfirmPassword,
  onSubmit,
  loading = false,
  minLength = 6,
}) {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const passwordsMatch = !confirmPassword || newPassword === confirmPassword;

  return (
    <BrandDialogFrame
      open={open}
      onClose={onClose}
      title="Reset Password"
      subtitle="Set a new password for the selected user."
      tone="purple"
      icon={LockOutlinedIcon}
      maxWidth="sm"
      footer={
        <BrandDialogFooter
          onCancel={onClose}
          onConfirm={onSubmit}
          confirmLabel="Reset Password"
          confirmTone="purple"
          loading={loading}
        />
      }
    >
      <Stack spacing={2}>
        <Alert severity="info" sx={{ borderRadius: 3, backgroundColor: "#f5f3ff", color: "#5b21b6" }}>
          Password must be strong and unique to keep the account secure.
        </Alert>

        <TextField
          fullWidth
          label="New Password *"
          type={showNewPassword ? "text" : "password"}
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          helperText={`Minimum ${minLength} characters`}
          inputProps={{ minLength }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowNewPassword((prev) => !prev)}>
                  {showNewPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label="Confirm Password *"
          type={showConfirmPassword ? "text" : "password"}
          value={confirmPassword}
          error={!passwordsMatch}
          onChange={(event) => setConfirmPassword(event.target.value)}
          helperText={passwordsMatch ? "Passwords must match" : "Passwords do not match"}
          inputProps={{ minLength }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)}>
                  {showConfirmPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>
    </BrandDialogFrame>
  );
}

export function BrandSelectionDialog({
  open,
  onClose,
  title,
  subtitle,
  tone = "green",
  search,
  onSearchChange,
  searchPlaceholder = "Search...",
  items,
  selectedId,
  onSelect,
  getPrimaryText,
  getSecondaryText,
  getBadgeText,
  countLabel,
  actionLabel,
  actionTone = "green",
  onAction,
  actionDisabled = false,
  loading = false,
}) {
  return (
    <BrandDialogFrame
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      tone={tone}
      icon={PersonSearchRoundedIcon}
      maxWidth="md"
      footer={
        <BrandDialogFooter
          onCancel={onClose}
          onConfirm={onAction}
          confirmLabel={actionLabel}
          confirmTone={actionTone}
          loading={loading}
          disabled={actionDisabled}
        />
      }
    >
      <Stack spacing={2}>
        <TextField
          fullWidth
          placeholder={searchPlaceholder}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#64748b" }} />
              </InputAdornment>
            ),
          }}
        />

        <Paper
          variant="outlined"
          sx={{
            maxHeight: 380,
            overflow: "auto",
            borderRadius: 3,
            borderColor: "#dbe4f0",
            backgroundColor: "#fff",
          }}
        >
          {items.map((item) => {
            const active = String(selectedId || "") === String(item.id);
            return (
              <Box
                key={item.id}
                onClick={() => onSelect(item)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  px: 1.5,
                  py: 1.25,
                  cursor: "pointer",
                  borderBottom: "1px solid #edf2f7",
                  background: active ? "linear-gradient(90deg, #ecfdf5, #f0fdf4)" : "#fff",
                }}
              >
                <Checkbox
                  checked={active}
                  color="success"
                  sx={{ p: 0.5 }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 800, color: "#0f172a" }}>{getPrimaryText(item)}</Typography>
                  <Typography sx={{ fontSize: 13.5, color: "#64748b" }}>{getSecondaryText(item)}</Typography>
                </Box>
                {getBadgeText ? (
                  <Paper
                    sx={{
                      px: 1.15,
                      py: 0.4,
                      borderRadius: 999,
                      backgroundColor: "#dcfce7",
                      color: "#15803d",
                      fontSize: 13,
                      fontWeight: 800,
                      boxShadow: "none",
                    }}
                  >
                    <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 800 }} primary={getBadgeText(item)} />
                  </Paper>
                ) : null}
              </Box>
            );
          })}

          {!items.length ? (
            <Typography sx={{ py: 4, textAlign: "center", color: "#64748b" }}>
              No items found.
            </Typography>
          ) : null}
        </Paper>

        {countLabel ? (
          <Typography sx={{ fontSize: 13.5, color: "#475569", fontWeight: 700 }}>
            {countLabel}
          </Typography>
        ) : null}
      </Stack>
    </BrandDialogFrame>
  );
}

export function BrandDeviceCard({ label = "Selected Device", title, subtitle }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.6,
        borderRadius: 3,
        borderColor: "#cfe0fb",
        background: "linear-gradient(180deg, rgba(239,246,255,0.92) 0%, rgba(255,255,255,0.96) 100%)",
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#64748b" }}>{label}</Typography>
      <Typography sx={{ mt: 0.6, fontSize: 22, fontWeight: 900, color: "#0f172a" }}>{title}</Typography>
      {subtitle ? (
        <Typography sx={{ mt: 0.35, fontSize: 14, color: "#64748b" }}>{subtitle}</Typography>
      ) : null}
    </Paper>
  );
}
